import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth, api } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { ArrowRight, ArrowLeft, Loader2, Check, User, Building2, Instagram, Youtube, MapPin, Globe } from 'lucide-react';

const NICHES = ['Fashion', 'Fitness', 'Tech', 'Beauty', 'Food', 'Travel', 'Lifestyle', 'Gaming', 'Music', 'Education'];
const PLATFORMS = ['Instagram', 'TikTok', 'YouTube', 'Twitter', 'LinkedIn', 'Facebook'];
const FOLLOWER_RANGES = [
  'Below 1k', '1k-5k', '5k-10k', '10k-25k', '25k-50k', '50k-100k', '100k-250k', '250k-500k', '500k+'
];
const BUDGET_RANGES = [
  'Gifted', 'Under $50', '$50-$100', '$100-$250', '$250-$500', '$500-$1000', '$1000-$5000', '$5000+'
];
const INDUSTRIES = ['Fashion', 'Tech', 'Food & Beverage', 'Health & Wellness', 'Beauty', 'Travel', 'Finance', 'Education', 'Entertainment', 'Other'];

// Supported Countries
const COUNTRIES = [
  { code: 'US', name: 'United States', currency: 'USD', symbol: '$' },
  { code: 'IN', name: 'India', currency: 'INR', symbol: '₹' },
  { code: 'GB', name: 'United Kingdom', currency: 'GBP', symbol: '£' },
  { code: 'CA', name: 'Canada', currency: 'CAD', symbol: 'C$' },
  { code: 'AU', name: 'Australia', currency: 'AUD', symbol: 'A$' },
  { code: 'DE', name: 'Germany', currency: 'EUR', symbol: '€' },
  { code: 'FR', name: 'France', currency: 'EUR', symbol: '€' },
  { code: 'JP', name: 'Japan', currency: 'JPY', symbol: '¥' },
  { code: 'BR', name: 'Brazil', currency: 'BRL', symbol: 'R$' },
  { code: 'MX', name: 'Mexico', currency: 'MXN', symbol: 'MX$' },
  { code: 'AE', name: 'UAE', currency: 'AED', symbol: 'د.إ' },
  { code: 'SG', name: 'Singapore', currency: 'SGD', symbol: 'S$' },
];

const Onboarding = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, updateUser, isAuthenticated, loading: authLoading } = useAuth();
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [role, setRole] = useState(user?.role || '');

  // Location state
  const [locationData, setLocationData] = useState({
    country_code: '',
    city: '',
    state: ''
  });

  // Creator profile state
  const [creatorData, setCreatorData] = useState({
    niches: [],
    follower_range: '',
    platforms: [],
    bio: '',
    portfolio_url: '',
    instagram_handle: '',
    tiktok_handle: '',
    youtube_handle: '',
    location: '',
    rate_per_post: 0,
    country_code: '',
    city: '',
    state: '',
    service_regions: []
  });

  // Brand profile state
  const [brandData, setBrandData] = useState({
    company_name: '',
    industry: '',
    budget_range: '',
    website: '',
    description: '',
    logo_url: '',
    location: '',
    country_code: '',
    city: '',
    state: '',
    target_regions: []
  });

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
    if (user?.role) {
      setRole(user.role);
      if (user.onboarding_completed) {
        navigate(user.role === 'creator' ? '/creator' : '/brand');
      } else {
        // User has role but hasn't completed onboarding - go to step 2
        setStep(2);
      }
    }
  }, [user, isAuthenticated, authLoading, navigate]);

  const handleRoleSelect = async (selectedRole) => {
    setRole(selectedRole);
    setLoading(true);
    try {
      const response = await api.put('/users/role', { role: selectedRole });
      updateUser(response.data);
      setStep(2);
    } catch (error) {
      toast.error('Failed to set role');
    } finally {
      setLoading(false);
    }
  };

  const handleNicheToggle = (niche) => {
    setCreatorData(prev => ({
      ...prev,
      niches: prev.niches.includes(niche)
        ? prev.niches.filter(n => n !== niche)
        : [...prev.niches, niche]
    }));
  };

  const handlePlatformToggle = (platform) => {
    setCreatorData(prev => ({
      ...prev,
      platforms: prev.platforms.includes(platform)
        ? prev.platforms.filter(p => p !== platform)
        : [...prev.platforms, platform]
    }));
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      // First verify location
      if (locationData.country_code) {
        await api.post(`/location/verify?country_code=${locationData.country_code}&city=${locationData.city}&state=${locationData.state}`);
      }

      // Update profile with location data
      const profileData = role === 'creator' 
        ? { 
            creator_profile: {
              ...creatorData,
              country_code: locationData.country_code,
              city: locationData.city,
              state: locationData.state
            }, 
            onboarding_completed: true 
          }
        : { 
            brand_profile: {
              ...brandData,
              country_code: locationData.country_code,
              city: locationData.city,
              state: locationData.state
            }, 
            onboarding_completed: true 
          };

      const response = await api.put('/users/profile', profileData);
      updateUser(response.data);
      toast.success('Profile completed!');
      navigate(role === 'creator' ? '/creator' : '/brand');
    } catch (error) {
      toast.error('Failed to save profile');
    } finally {
      setLoading(false);
    }
  };

  // Get selected country info
  const selectedCountry = COUNTRIES.find(c => c.code === locationData.country_code);

  if (authLoading) {
    return (
      <div className="min-h-screen dashboard-bg flex items-center justify-center">
        <div className="floating-orb floating-orb-1"></div>
        <div className="floating-orb floating-orb-2"></div>
        <div className="glass-card p-8 rounded-2xl">
          <Loader2 className="w-8 h-8 animate-spin text-[#3AAFB9]" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen dashboard-bg relative overflow-hidden">
      {/* Creative Background Elements */}
      <div className="fixed inset-0 pointer-events-none z-0">
        {/* Vibrant Orbs */}
        <div className="creator-orb creator-orb-pink" style={{ top: '-10%', right: '5%', opacity: 0.4 }} />
        <div className="creator-orb creator-orb-teal" style={{ bottom: '15%', left: '-5%', opacity: 0.35 }} />
        <div className="creator-orb creator-orb-orange" style={{ top: '50%', right: '-10%', opacity: 0.3 }} />
        
        {/* Network Lines */}
        <div className="network-lines opacity-30" />
      </div>
      
      {/* Progress Bar */}
      <div className="fixed top-0 left-0 right-0 h-1 bg-gray-200 z-50">
        <div 
          className="h-full bg-gradient-to-r from-[#3AAFB9] to-[#E8795A] transition-all duration-300"
          style={{ width: `${(step / 3) * 100}%` }}
        ></div>
      </div>

      <div className="max-w-2xl mx-auto px-6 py-12 relative z-10">
        {/* Logo */}
        <div className="flex items-center gap-3 mb-12">
          <img 
            src="https://customer-assets.emergentagent.com/job_creator-brand-2/artifacts/feyyw8lo_145437-200.png" 
            alt="CmdOut" 
            className="w-12 h-12"
          />
          <div>
            <span className="font-bold text-xl tracking-tight">CmdOut</span>
            <span className="text-[#3AAFB9] font-bold">.io</span>
          </div>
        </div>

        {/* Step 1: Role Selection */}
        {step === 1 && !role && (
          <div className="animate-fade-in">
            <h1 className="text-3xl font-bold mb-2 text-gray-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              Welcome to CmdOut
            </h1>
            <p className="text-gray-500 mb-8">
              Let's get you set up. First, tell us who you are.
            </p>

            <div className="grid gap-4">
              <button
                onClick={() => handleRoleSelect('creator')}
                disabled={loading}
                className="group relative p-6 rounded-2xl border-2 border-gray-200 hover:border-[#E8795A] glass-card transition-all text-left"
                data-testid="onboard-creator-btn"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#E8795A]/20 flex items-center justify-center">
                    <User className="w-6 h-6 text-[#E8795A]" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold mb-1">I'm a Creator</h3>
                    <p className="text-muted-foreground">
                      Discover brand collaborations that match your niche and audience.
                    </p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-[#E8795A] transition-colors" />
                </div>
              </button>

              <button
                onClick={() => handleRoleSelect('brand')}
                disabled={loading}
                className="group relative p-6 rounded-2xl border-2 border-border hover:border-[#3AAFB9] bg-card transition-all text-left"
                data-testid="onboard-brand-btn"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#3AAFB9]/20 flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-[#3AAFB9]" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold mb-1">I'm a Brand</h3>
                    <p className="text-muted-foreground">
                      Connect with creators who align with your audience and goals.
                    </p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-[#3AAFB9] transition-colors" />
                </div>
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Basic Info */}
        {step === 2 && (
          <div className="animate-fade-in">
            <div className="flex items-center justify-between mb-8">
              <button 
                onClick={() => setStep(1)}
                className="flex items-center gap-2 text-gray-500 hover:text-gray-900"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>
              <button 
                onClick={() => navigate(role === 'creator' ? '/creator' : '/brand')}
                className="text-sm text-gray-500 hover:text-gray-900 underline"
                data-testid="skip-onboarding-btn"
              >
                Skip for now
              </button>
            </div>

            <h1 className="text-3xl font-bold mb-2 text-gray-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              {role === 'creator' ? 'Tell us about your content' : 'Tell us about your brand'}
            </h1>
            <p className="text-gray-500 mb-8">
              This helps us match you with the right {role === 'creator' ? 'opportunities' : 'creators'}.
            </p>

            {/* Location Selection - Common for both roles */}
            <div className="glass-card rounded-2xl p-6 mb-6 border border-[#3AAFB9]/20">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#3AAFB9] to-[#3AAFB9]/70 flex items-center justify-center">
                  <Globe className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Your Location</h3>
                  <p className="text-xs text-gray-500">This determines which campaigns you'll see</p>
                </div>
              </div>
              
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-sm font-medium mb-2 block text-gray-700">Country *</Label>
                  <Select 
                    value={locationData.country_code} 
                    onValueChange={(v) => setLocationData(prev => ({ ...prev, country_code: v }))}
                  >
                    <SelectTrigger data-testid="country-select" className="bg-white">
                      <SelectValue placeholder="Select your country" />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRIES.map(country => (
                        <SelectItem key={country.code} value={country.code}>
                          <span className="flex items-center gap-2">
                            {country.name}
                            <span className="text-xs text-gray-400">({country.symbol})</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm font-medium mb-2 block text-gray-700">State/Province</Label>
                  <Input
                    placeholder="e.g., California"
                    value={locationData.state}
                    onChange={(e) => setLocationData(prev => ({ ...prev, state: e.target.value }))}
                    className="bg-white"
                    data-testid="state-input"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium mb-2 block text-gray-700">City</Label>
                  <Input
                    placeholder="e.g., Los Angeles"
                    value={locationData.city}
                    onChange={(e) => setLocationData(prev => ({ ...prev, city: e.target.value }))}
                    className="bg-white"
                    data-testid="city-input"
                  />
                </div>
              </div>
              
              {selectedCountry && (
                <div className="mt-4 p-3 bg-[#3AAFB9]/10 rounded-lg">
                  <p className="text-sm text-[#3AAFB9]">
                    <MapPin className="w-4 h-4 inline mr-1" />
                    Currency: <strong>{selectedCountry.currency}</strong> ({selectedCountry.symbol})
                  </p>
                </div>
              )}
            </div>

            {role === 'creator' ? (
              <div className="space-y-6">
                {/* Niches */}
                <div>
                  <Label className="text-sm font-medium mb-3 block text-gray-700">Your Niches (select all that apply)</Label>
                  <div className="flex flex-wrap gap-2">
                    {NICHES.map(niche => (
                      <button
                        key={niche}
                        onClick={() => handleNicheToggle(niche)}
                        className={`px-4 py-2 rounded-full text-sm transition-all ${
                          creatorData.niches.includes(niche)
                            ? 'bg-[#E8795A] text-white shadow-lg shadow-[#E8795A]/25'
                            : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                        }`}
                        data-testid={`niche-${niche.toLowerCase()}`}
                      >
                        {niche}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Follower Range */}
                <div>
                  <Label className="text-sm font-medium mb-3 block text-gray-700">Follower Count</Label>
                  <Select 
                    value={creatorData.follower_range} 
                    onValueChange={(v) => setCreatorData(prev => ({ ...prev, follower_range: v }))}
                  >
                    <SelectTrigger data-testid="follower-select" className="bg-white">
                      <SelectValue placeholder="Select your follower range" />
                    </SelectTrigger>
                    <SelectContent>
                      {FOLLOWER_RANGES.map(range => (
                        <SelectItem key={range} value={range}>{range}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Platforms */}
                <div>
                  <Label className="text-sm font-medium mb-3 block text-gray-700">Platforms</Label>
                  <div className="flex flex-wrap gap-2">
                    {PLATFORMS.map(platform => (
                      <button
                        key={platform}
                        onClick={() => handlePlatformToggle(platform)}
                        className={`px-4 py-2 rounded-full text-sm transition-all ${
                          creatorData.platforms.includes(platform)
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-secondary hover:bg-secondary/80'
                        }`}
                        data-testid={`platform-${platform.toLowerCase()}`}
                      >
                        {platform}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Company Name */}
                <div>
                  <Label htmlFor="company" className="text-sm font-medium mb-2 block">Company Name</Label>
                  <Input
                    id="company"
                    placeholder="Your company name"
                    value={brandData.company_name}
                    onChange={(e) => setBrandData(prev => ({ ...prev, company_name: e.target.value }))}
                    className="h-12"
                    data-testid="company-input"
                  />
                </div>

                {/* Industry */}
                <div>
                  <Label className="text-sm font-medium mb-3 block">Industry</Label>
                  <Select 
                    value={brandData.industry} 
                    onValueChange={(v) => setBrandData(prev => ({ ...prev, industry: v }))}
                  >
                    <SelectTrigger data-testid="industry-select">
                      <SelectValue placeholder="Select your industry" />
                    </SelectTrigger>
                    <SelectContent>
                      {INDUSTRIES.map(ind => (
                        <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Budget Range */}
                <div>
                  <Label className="text-sm font-medium mb-3 block">Typical Campaign Budget</Label>
                  <Select 
                    value={brandData.budget_range} 
                    onValueChange={(v) => setBrandData(prev => ({ ...prev, budget_range: v }))}
                  >
                    <SelectTrigger data-testid="budget-select">
                      <SelectValue placeholder="Select budget range" />
                    </SelectTrigger>
                    <SelectContent>
                      {BUDGET_RANGES.map(range => (
                        <SelectItem key={range} value={range}>{range}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Website */}
                <div>
                  <Label htmlFor="website" className="text-sm font-medium mb-2 block">Website</Label>
                  <Input
                    id="website"
                    type="url"
                    placeholder="https://yourcompany.com"
                    value={brandData.website}
                    onChange={(e) => setBrandData(prev => ({ ...prev, website: e.target.value }))}
                    className="h-12"
                    data-testid="website-input"
                  />
                </div>
              </div>
            )}

            <Button 
              onClick={() => setStep(3)}
              className="w-full mt-8 h-12 rounded-full"
              disabled={role === 'creator' ? creatorData.niches.length === 0 : !brandData.company_name}
              data-testid="next-step-btn"
            >
              Continue
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </div>
        )}

        {/* Step 3: Additional Details */}
        {step === 3 && (
          <div className="animate-fade-in">
            <button 
              onClick={() => setStep(2)}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>

            <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              Almost there!
            </h1>
            <p className="text-muted-foreground mb-8">
              Add a few more details to complete your profile.
            </p>

            {role === 'creator' ? (
              <div className="space-y-6">
                {/* Bio */}
                <div>
                  <Label htmlFor="bio" className="text-sm font-medium mb-2 block">Bio</Label>
                  <Textarea
                    id="bio"
                    placeholder="Tell brands about yourself and your content..."
                    value={creatorData.bio}
                    onChange={(e) => setCreatorData(prev => ({ ...prev, bio: e.target.value }))}
                    rows={4}
                    data-testid="bio-input"
                  />
                </div>

                {/* Social Handles */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium mb-2 block">Instagram Handle</Label>
                    <div className="relative">
                      <Instagram className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input
                        placeholder="@username"
                        value={creatorData.instagram_handle}
                        onChange={(e) => setCreatorData(prev => ({ ...prev, instagram_handle: e.target.value }))}
                        className="pl-10 h-12"
                        data-testid="instagram-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm font-medium mb-2 block">YouTube Channel</Label>
                    <div className="relative">
                      <Youtube className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input
                        placeholder="Channel name"
                        value={creatorData.youtube_handle}
                        onChange={(e) => setCreatorData(prev => ({ ...prev, youtube_handle: e.target.value }))}
                        className="pl-10 h-12"
                        data-testid="youtube-input"
                      />
                    </div>
                  </div>
                </div>

                {/* Rate */}
                <div>
                  <Label htmlFor="rate" className="text-sm font-medium mb-2 block">Rate per Post ($)</Label>
                  <Input
                    id="rate"
                    type="number"
                    placeholder="100"
                    value={creatorData.rate_per_post || ''}
                    onChange={(e) => setCreatorData(prev => ({ ...prev, rate_per_post: parseFloat(e.target.value) || 0 }))}
                    className="h-12"
                    data-testid="rate-input"
                  />
                </div>

                {/* Location */}
                <div>
                  <Label htmlFor="location" className="text-sm font-medium mb-2 block">Location</Label>
                  <Input
                    id="location"
                    placeholder="City, Country"
                    value={creatorData.location}
                    onChange={(e) => setCreatorData(prev => ({ ...prev, location: e.target.value }))}
                    className="h-12"
                    data-testid="location-input"
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Description */}
                <div>
                  <Label htmlFor="description" className="text-sm font-medium mb-2 block">Company Description</Label>
                  <Textarea
                    id="description"
                    placeholder="Tell creators about your brand and what you're looking for..."
                    value={brandData.description}
                    onChange={(e) => setBrandData(prev => ({ ...prev, description: e.target.value }))}
                    rows={4}
                    data-testid="description-input"
                  />
                </div>

                {/* Logo URL */}
                <div>
                  <Label htmlFor="logo" className="text-sm font-medium mb-2 block">Logo URL</Label>
                  <Input
                    id="logo"
                    type="url"
                    placeholder="https://yourcompany.com/logo.png"
                    value={brandData.logo_url}
                    onChange={(e) => setBrandData(prev => ({ ...prev, logo_url: e.target.value }))}
                    className="h-12"
                    data-testid="logo-input"
                  />
                </div>

                {/* Location */}
                <div>
                  <Label htmlFor="brand-location" className="text-sm font-medium mb-2 block">Location</Label>
                  <Input
                    id="brand-location"
                    placeholder="City, Country"
                    value={brandData.location}
                    onChange={(e) => setBrandData(prev => ({ ...prev, location: e.target.value }))}
                    className="h-12"
                    data-testid="brand-location-input"
                  />
                </div>
              </div>
            )}

            <Button 
              onClick={handleComplete}
              className="w-full mt-8 h-12 rounded-full btn-glow"
              disabled={loading}
              data-testid="complete-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  Complete Setup
                  <Check className="ml-2 w-4 h-4" />
                </>
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Onboarding;
